import { useState, useMemo, useEffect } from "react";
import { T, ALL_TYPES } from "./constants";
import Header from "./components/Header";
import FilterBar from "./components/FilterBar";
import RemedyCard from "./components/RemedyCard";
import Modal from "./components/Modal";
import Footer from "./components/Footer";

export default function EarthyMujer() {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [search, setSearch]     = useState("");
  const [type, setType]         = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch("https://botanica-production.up.railway.app/remedies")
      .then(res => { if (!res.ok) throw new Error("API error"); return res.json(); })
      .then(json => { setData(json.remedies || []); setLoading(false); })
      .catch(err => { console.error(err); setError(true); setLoading(false); });
  }, []);

  const typeCounts = useMemo(() => {
    const counts = {};
    data.forEach(r => { counts[r.type] = (counts[r.type] || 0) + 1; });
    return counts;
  }, [data]);

  const filtered = useMemo(() => data.filter(item => {
    const matchType = type === "all" || item.type === type;
    const q = search.toLowerCase().trim();
    const matchSearch = !q
      || item.name.toLowerCase().includes(q)
      || item.description.toLowerCase().includes(q)
      || item.properties.some(p => p.toLowerCase().includes(q))
      || item.ingredients.some(i => i.name.toLowerCase().includes(q));
    return matchType && matchSearch;
  }), [data, search, type]);

  const filterButtons = [
    { key: "all", label: "All" },
    ...ALL_TYPES
      .filter(t => (typeCounts[t] || 0) > 0)
      .map(t => ({ key: t, label: t.charAt(0).toUpperCase() + t.slice(1) + "s" })),
  ];

  return (
    <div style={{ minHeight: "100vh", background: T.linen, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');
        * { box-sizing: border-box; }
        ::placeholder { color: rgba(240,247,235,0.35); }
        input:focus { outline: none; }
      `}</style>

      <Header search={search} onSearchChange={setSearch} />

      <FilterBar
        filterButtons={filterButtons}
        activeType={type}
        onTypeChange={setType}
      />

      <div style={{ padding: "0.85rem 2rem 0.25rem", maxWidth: "1100px", margin: "0 auto" }}>
        <p style={{ fontSize: "0.75rem", color: T.muted, letterSpacing: "0.05em" }}>
          {loading
            ? "Gathering remedies..."
            : `${filtered.length} ${filtered.length === 1 ? "remedy" : "remedies"}${search ? ` for "${search}"` : ""}${type !== "all" ? ` · ${type}s` : ""}`
          }
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "5rem 2rem", color: T.muted }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🌿</div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: "1.2rem", color: T.bark }}>
            Gathering from the garden...
          </p>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "5rem 2rem", color: T.muted }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🍂</div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: "1.2rem", color: T.bark }}>
            Couldn't reach the garden right now.
          </p>
          <p style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>Check your connection and try again.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "5rem 2rem", color: T.muted }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🌱</div>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: "1.2rem", color: T.bark }}>
            Nothing found for "{search}"
          </p>
          <p style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>Try "sleep", "immune", "cough", or "detox"</p>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "1px",
          background: T.linen3,
          maxWidth: "1100px",
          margin: "0.5rem auto 0",
          borderTop: `1px solid ${T.linen3}`,
          borderBottom: `1px solid ${T.linen3}`,
        }}>
          {filtered.map(item => (
            <RemedyCard key={item.id} item={item} onClick={setSelected} />
          ))}
        </div>
      )}

      <div style={{ padding: "3rem 0" }} />

      <Modal item={selected} onClose={() => setSelected(null)} />

      <Footer />
    </div>
  );
}
